import React, { useMemo } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

const HeatmapCalendar = ({ data = [], year = new Date().getFullYear(), language = "en" }) => {
  const isEnglish = language === "en";

  // Group data by date
  const dataByDate = useMemo(() => {
    const map = {};
    data.forEach((item) => {
      const date = item.date; // YYYY-MM-DD format
      map[date] = (map[date] || 0) + (item.count || item.value || 1);
    });
    return map;
  }, [data]);

  // Generate calendar grid for the year
  const calendar = useMemo(() => {
    const weeks = [];
    const startDate = new Date(year, 0, 1);
    const endDate = new Date(year, 11, 31);

    // Start from the first Sunday of the year (or earlier to include Jan 1)
    const firstSunday = new Date(startDate);
    firstSunday.setDate(startDate.getDate() - startDate.getDay());

    let currentDate = new Date(firstSunday);
    let week = [];

    while (currentDate <= endDate || week.length > 0) {
      if (currentDate.getDay() === 0 && week.length > 0) {
        weeks.push(week);
        week = [];
      }

      const dateStr = currentDate.toISOString().split("T")[0];
      const isCurrentYear = currentDate.getFullYear() === year;

      week.push({
        date: dateStr,
        value: isCurrentYear ? (dataByDate[dateStr] || 0) : null,
        isCurrentYear,
        dayOfWeek: currentDate.getDay(),
      });

      currentDate.setDate(currentDate.getDate() + 1);

      if (currentDate > endDate && week.length === 7) {
        weeks.push(week);
        break;
      }
    }

    return weeks;
  }, [year, dataByDate]);

  // Get color based on value
  const getColor = (value) => {
    if (value === null || value === undefined) return "transparent";
    if (value === 0) return "rgba(203, 213, 225, 0.3)"; // Light gray for no activity
    if (value <= 2) return "rgba(34, 197, 94, 0.3)"; // Light green
    if (value <= 4) return "rgba(34, 197, 94, 0.6)"; // Medium green
    if (value <= 6) return "rgba(34, 197, 94, 0.8)"; // Dark green
    return "rgba(22, 163, 74, 1)"; // Very dark green
  };

  const getMonthLabel = (monthIndex) => {
    const months = isEnglish
      ? ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
      : ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agt", "Sep", "Okt", "Nov", "Des"];
    return months[monthIndex];
  };

  const getDayLabel = (dayIndex) => {
    const days = isEnglish ? ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] : ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];
    return days[dayIndex];
  };

  // Calculate stats
  const stats = useMemo(() => {
    const values = Object.values(dataByDate);
    const totalDays = Object.keys(dataByDate).length;
    const totalActivities = values.reduce((sum, val) => sum + val, 0);
    const average = totalDays > 0 ? (totalActivities / totalDays).toFixed(1) : 0;
    const maxDay = Math.max(...values, 0);

    return { totalDays, totalActivities, average, maxDay };
  }, [dataByDate]);

  return (
    <div className="space-y-4">
      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-muted/50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-primary">{stats.totalDays}</p>
          <p className="text-xs text-muted-foreground">{isEnglish ? "Active Days" : "Hari Aktif"}</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-primary">{stats.totalActivities}</p>
          <p className="text-xs text-muted-foreground">{isEnglish ? "Total Activities" : "Total Aktivitas"}</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-primary">{stats.average}</p>
          <p className="text-xs text-muted-foreground">{isEnglish ? "Daily Average" : "Rata-rata Harian"}</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-primary">{stats.maxDay}</p>
          <p className="text-xs text-muted-foreground">{isEnglish ? "Best Day" : "Hari Terbaik"}</p>
        </div>
      </div>

      {/* Calendar */}
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          {/* Month labels */}
          <div className="flex mb-2 pl-8">
            {calendar.length > 0 && (
              <>
                {Array.from({ length: 12 }).map((_, i) => {
                  const monthStart = new Date(year, i, 1);
                  const monthEnd = new Date(year, i + 1, 0);
                  const firstWeek = Math.floor((monthStart.getTime() - new Date(year, 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000));
                  const weekSpan = Math.ceil((monthEnd.getDate() + (monthStart.getDay() || 7)) / 7);

                  return (
                    <div
                      key={i}
                      className="text-xs text-muted-foreground"
                      style={{
                        marginLeft: i === 0 ? `${firstWeek * 14}px` : 0,
                        width: `${weekSpan * 14 - 2}px`,
                      }}
                    >
                      {getMonthLabel(i)}
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Day labels + Grid */}
          <div className="flex gap-1">
            {/* Day of week labels */}
            <div className="flex flex-col gap-1 text-xs text-muted-foreground pr-2">
              <div className="h-[12px]"></div>
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="h-[12px] flex items-center">
                  {i % 2 === 1 ? getDayLabel(i) : ""}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <TooltipProvider>
              <div className="flex gap-1">
                {calendar.map((week, weekIndex) => (
                  <div key={weekIndex} className="flex flex-col gap-1">
                    {week.map((day, dayIndex) => (
                      <Tooltip key={dayIndex}>
                        <TooltipTrigger asChild>
                          <div
                            className="w-[12px] h-[12px] rounded-sm transition-all hover:ring-2 hover:ring-primary/50 cursor-pointer"
                            style={{
                              backgroundColor: getColor(day.value),
                            }}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          {day.value !== null ? (
                            <div className="text-xs">
                              <p className="font-medium">
                                {new Date(day.date).toLocaleDateString(isEnglish ? "en-US" : "id-ID", {
                                  weekday: "long",
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                })}
                              </p>
                              <p className="text-muted-foreground">
                                {isEnglish ? "Activities" : "Aktivitas"}: <strong>{day.value}</strong>
                              </p>
                            </div>
                          ) : (
                            <div className="text-xs text-muted-foreground">
                              {isEnglish ? "Not in this year" : "Bukan tahun ini"}
                            </div>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                ))}
              </div>
            </TooltipProvider>
          </div>

          {/* Legend */}
          <div className="flex items-center justify-end gap-2 mt-3 pl-8">
            <span className="text-xs text-muted-foreground">{isEnglish ? "Less" : "Lebih Sedikit"}</span>
            <div className="flex gap-1">
              {[0, 1, 2, 3, 4].map((level) => (
                <div
                  key={level}
                  className="w-[12px] h-[12px] rounded-sm"
                  style={{
                    backgroundColor: getColor(level === 0 ? 0 : level * 2),
                  }}
                />
              ))}
            </div>
            <span className="text-xs text-muted-foreground">{isEnglish ? "More" : "Lebih Banyak"}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeatmapCalendar;
